// The write half of the API surface.
//
// Each function is a thin wrapper over api.*, paired with the query keys it
// should invalidate. Those key lists are the direct translation of the old
// `revalidatePath` calls — where a Server Action revalidated "/album" and
// "/timeline", the equivalent here invalidates keys.photos() and keys.timeline.

import type {
  CalendarEvent,
  CreateAlbum,
  CreateEvent,
  CreateIllustration,
  CreateMessage,
  CreateMilestone,
  CreatePhoto,
  CreatePlace,
  GuessResult,
  HintResult,
  MovePhotosToPlace,
  NotificationList,
  PhotoDownload,
  PushSubscriptionInput,
  Place,
  SubmitGameResult,
  SubmitGuess,
  SubmitWord,
  TimedGameId,
  TimedRoundView,
  UpdateAlbum,
  UpdateEvent,
  UpdateIllustration,
  UpdateMessage,
  UpdateMilestone,
  UpdatePhoto,
  UpdatePlace,
} from "@historia/shared/contracts";
import { api } from "./client";
import { keys } from "./queries";

// ------------------------------------------------------------
//  Invalidation sets — one place, so a mutation can't forget a page.
// ------------------------------------------------------------

/** Anything touching photos also changes the timeline and the album covers. */
export const AFTER_PHOTO_CHANGE = [
  ["photos"],
  keys.timeline,
  keys.albumsWithCounts,
  keys.numbers,
  keys.messages, // a message's featured photo may have gone
];

export const AFTER_MILESTONE_CHANGE = [keys.timeline, ["milestones"], keys.numbers];
export const AFTER_ALBUM_CHANGE = [["albums"], ["photos"]];
export const AFTER_ILLUSTRATION_CHANGE = [keys.illustrations, ["photos"], keys.numbers];
export const AFTER_MESSAGE_CHANGE = [keys.messages, keys.numbers];
export const AFTER_GAME_CHANGE = [["games"]];
/**
 * Reading, or being sent, a notification.
 *
 * `["notifications"]` covers both the list and the unread count — the count's
 * key is `["notifications", "unread"]`, a child of it, so one entry keeps the
 * page and the badge from ever disagreeing about how many are unread.
 */
export const AFTER_NOTIFICATION_CHANGE = [["notifications"]];

// ------------------------------------------------------------
//  Milestones
// ------------------------------------------------------------

export const createMilestone = (input: CreateMilestone) => api.post("/milestones", input);
export const updateMilestone = ({ id, ...input }: UpdateMilestone & { id: string }) =>
  api.patch(`/milestones/${id}`, input);
export const deleteMilestone = (id: string) => api.delete(`/milestones/${id}`);

// ------------------------------------------------------------
//  Albums
// ------------------------------------------------------------

export const createAlbum = (input: CreateAlbum) => api.post("/albums", input);
export const updateAlbum = ({ id, ...input }: UpdateAlbum & { id: string }) =>
  api.patch(`/albums/${id}`, input);
export const deleteAlbum = (id: string) => api.delete(`/albums/${id}`);

// ------------------------------------------------------------
//  Photos
// ------------------------------------------------------------

export const createPhoto = (input: CreatePhoto) => api.post("/photos", input);
export const updatePhoto = ({ id, ...input }: UpdatePhoto & { id: string }) =>
  api.patch(`/photos/${id}`, input);
export const deletePhoto = (id: string) => api.delete(`/photos/${id}`);

/** Toggle the anniversary page's featured photo. */
export const toggleFeatured = (id: string) =>
  api.put<{ destaque: boolean }>(`/photos/${id}/featured`);

export const movePhotos = (input: { ids: string[]; albumId: string | null }) =>
  api.patch<{ moved: number }>("/photos", input);

export const deletePhotos = (ids: string[]) =>
  api.delete<{ deleted: number }>("/photos", { ids });

/**
 * Asks for a one-shot link that saves the photo to the device (IDEA-018).
 *
 * A GET, but it lives here rather than in queries.ts because it isn't state to
 * cache: the link expires in five minutes, so caching one would mean handing
 * out a dead URL later.
 */
export const photoDownloadUrl = (id: string) =>
  api.get<PhotoDownload>(`/photos/${id}/download`);

// ------------------------------------------------------------
//  Illustrations
// ------------------------------------------------------------

export const createIllustration = (input: CreateIllustration) =>
  api.post("/illustrations", input);
export const updateIllustration = ({ id, ...input }: UpdateIllustration & { id: string }) =>
  api.patch(`/illustrations/${id}`, input);
export const deleteIllustration = (id: string) => api.delete(`/illustrations/${id}`);
export const reorderIllustration = (input: { id: string; direction: "up" | "down" }) =>
  api.post(`/illustrations/${input.id}/reorder`, { direction: input.direction });

// ------------------------------------------------------------
//  Messages
// ------------------------------------------------------------

export const createMessage = (input: CreateMessage) => api.post("/messages", input);
export const updateMessage = ({ id, ...input }: UpdateMessage & { id: string }) =>
  api.patch(`/messages/${id}`, input);
export const deleteMessage = (id: string) => api.delete(`/messages/${id}`);
export const reorderMessage = (input: { id: string; direction: "up" | "down" }) =>
  api.post(`/messages/${input.id}/reorder`, { direction: input.direction });

// ------------------------------------------------------------
//  Games
// ------------------------------------------------------------

export const createRound = () => api.post("/games/rounds");

export const submitWord = ({ roundId, ...input }: SubmitWord & { roundId: string }) =>
  api.post(`/games/rounds/${roundId}/words`, input);

export const submitGuess = ({ wordId, ...input }: SubmitGuess & { wordId: string }) =>
  api.post<GuessResult>(`/games/words/${wordId}/guesses`, input);

/** Give up on a word: it closes, and the round is lost. */
export const giveUpWord = ({ wordId, tempoMs }: { wordId: string; tempoMs: number }) =>
  api.post<{ palavra: string }>(`/games/words/${wordId}/give-up`, { tempoMs });

/** Spend a hint: one more letter uncovered, ten seconds onto the clock. */
export const spendHint = (wordId: string) =>
  api.post<HintResult>(`/games/words/${wordId}/hints`);

// ------------------------------------------------------------
//  Places — the map (IDEA-014)
// ------------------------------------------------------------

export const AFTER_PLACE_CHANGE = [["places"], keys.numbers, keys.home, ["photos"]];

export const createPlace = (input: CreatePlace) => api.post<Place>("/places", input);
export const updatePlace = ({ id, ...input }: UpdatePlace & { id: string }) =>
  api.patch<Place>(`/places/${id}`, input);
export const deletePlace = (id: string) => api.delete(`/places/${id}`);

/** Files photos under a place (or unfiles them with `lugarId: null`). */
export const movePhotosToPlace = (input: MovePhotosToPlace) =>
  api.patch<{ moved: number }>("/places/photos", input);

// ------------------------------------------------------------
//  Events — the shared calendar (IDEA-030)
// ------------------------------------------------------------

export const AFTER_EVENT_CHANGE = [["events"], keys.numbers, keys.home];

export const createEvent = (input: CreateEvent) => api.post<CalendarEvent>("/events", input);
export const updateEvent = ({ id, ...input }: UpdateEvent & { id: string }) =>
  api.patch<CalendarEvent>(`/events/${id}`, input);
export const deleteEvent = (id: string) => api.delete(`/events/${id}`);

// ------------------------------------------------------------
//  Timed games — Memória and Shikaku (IDEA-031, IDEA-032)
// ------------------------------------------------------------

/** A finished board also moves the scoreboard and the home screen's nudge. */
export const AFTER_TIMED_GAME_CHANGE = [["games"], keys.home];

export const createTimedRound = (jogo: TimedGameId) =>
  api.post<TimedRoundView>(`/games/${jogo}/rounds`);

export const submitGameResult = ({
  jogo,
  roundId,
  ...input
}: SubmitGameResult & { jogo: TimedGameId; roundId: string }) =>
  api.post<TimedRoundView>(`/games/${jogo}/rounds/${roundId}/results`, input);

// ------------------------------------------------------------
//  Notifications (IDEA-044)
// ------------------------------------------------------------

/**
 * Both return the refreshed list, so the page and its badge update from the
 * response instead of a second request chasing the first.
 */
export const markNotificationRead = (id: string) =>
  api.post<NotificationList>(`/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  api.post<NotificationList>("/notifications/read-all");

export const subscribeToPush = (input: PushSubscriptionInput) =>
  api.post<{ ok: true }>("/push/subscriptions", input);

export const unsubscribeFromPush = (endpoint: string) =>
  api.delete("/push/subscriptions", { endpoint });
